import { useState, useCallback, useEffect } from 'react';

// Keys for different tutorial/promo sections that can be dismissed
export type TutorialKey = 
  | 'tryon_model_avatars'
  | 'tryon_sample_looks'
  | 'first_item_tap_hint'
  | 'item_detail_first_view'
  | 'polls_community_welcome'
  | 'saves_welcome'
  | 'tryon_welcome'
  | 'chat_welcome'
  | 'collections_welcome'
  | 'save_tip_price_tools'
  | 'hamburger_menu_intro'
  | 'tryon_outfit_discovery'
  | 'first_outfit_created'
  | 'first_item_tryon_nudge'
  | 'share_from_anywhere_tip'
  // Add more tutorial keys here as needed

const STORAGE_KEY = 'dismissed_tutorials';

export function useDismissedTutorials() {
  const [dismissedTutorials, setDismissedTutorials] = useState<Set<TutorialKey>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return new Set(JSON.parse(stored) as TutorialKey[]);
      }
    } catch (e) {
      console.error('Error loading dismissed tutorials:', e);
    }
    return new Set();
  });

  // Persist to localStorage whenever the set changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(dismissedTutorials)));
  }, [dismissedTutorials]);

  const isDismissed = useCallback((key: TutorialKey) => {
    return dismissedTutorials.has(key);
  }, [dismissedTutorials]);

  const dismiss = useCallback((key: TutorialKey) => {
    setDismissedTutorials(prev => {
      const next = new Set(prev);
      next.add(key);
      // Persist immediately to avoid race conditions on unmount
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(next)));
      return next;
    });
  }, []);

  const resetAll = useCallback(() => {
    setDismissedTutorials(new Set());
    // Clear all dismissible prompt keys across the app
    [
      'tryonShowModelAvatars',
      'early-user-nudge-dismissed',
      'seyu_upload_tip_dismissed',
      'style_consultation_dismissed_at',
      'want_get_started_dismissed',
      'dismissed-promo-cards',
      'dismissed_announcements',
      'seyu_post_signup_complete',
      'seyu_onboarding_complete',
    ].forEach(key => localStorage.removeItem(key));
  }, []);

  const resetOne = useCallback((key: TutorialKey) => {
    setDismissedTutorials(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  return {
    isDismissed,
    dismiss,
    resetAll,
    resetOne,
    dismissedCount: dismissedTutorials.size,
  };
}
